const GENERIC_FONT_FAMILIES = new Set([
  "serif",
  "sans-serif",
  "monospace",
  "cursive",
  "fantasy",
  "system-ui",
  "ui-serif",
  "ui-sans-serif",
  "ui-monospace",
  "ui-rounded",
  "emoji",
  "math",
  "fangsong",
  "inherit",
  "initial",
  "revert",
  "revert-layer",
  "unset",
]);

export const READER_SETTINGS_STYLE_ID = "reader-custom-settings";
export const RENDITION_CUSTOM_THEME_STYLE_ID = "epubjs-inserted-css-custom";

export function resolveReaderFontFamilyCss(fontFamily?: string): string {
  if (!fontFamily || fontFamily === "default") {
    return "";
  }

  return `${fontFamily} !important`;
}

export function resolveEffectiveReaderFontFamily(
  fontFamily: string | undefined,
  hasPublisherFontFamily: boolean | undefined,
  fallbackFontFamily?: string,
): string | undefined {
  if (fontFamily && fontFamily !== "default") {
    return fontFamily;
  }

  if (hasPublisherFontFamily === true) {
    return "default";
  }

  if (fallbackFontFamily && fallbackFontFamily !== "default") {
    return fallbackFontFamily;
  }

  return fontFamily;
}

export function cssHasPublisherFontFamily(css: string): boolean {
  const cssWithoutComments = css.replace(/\/\*[\s\S]*?\*\//g, "");
  const declarations = cssWithoutComments.matchAll(/font-family\s*:\s*([^;{}]+)/gi);

  for (const declaration of declarations) {
    const families = declaration[1]
      .split(",")
      .map((family) =>
        family
          .trim()
          .replace(/\s*!important\s*$/i, "")
          .replace(/^["']|["']$/g, "")
          .trim()
          .toLowerCase(),
      )
      .filter(Boolean);

    if (families.some((family) => !GENERIC_FONT_FAMILIES.has(family))) {
      return true;
    }
  }

  return false;
}

const upsertReaderStyleElement = (
  doc: Document,
  id: string,
  cssText: string,
  dataSource: string,
): HTMLStyleElement => {
  let styleEl = doc.getElementById(id) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = doc.createElement("style");
    styleEl.id = id;
    doc.head.appendChild(styleEl);
  }
  styleEl.setAttribute("data-source", dataSource);
  styleEl.textContent = cssText;
  return styleEl;
};

export function syncReaderSettingsCss(doc: Document, cssText: string): void {
  // The rendition theme style is injected before doSettings measures computed
  // chapter font sizes. Keep it in lockstep with the immediate settings style
  // so stale selector overrides cannot compound across taps or section changes.
  upsertReaderStyleElement(
    doc,
    RENDITION_CUSTOM_THEME_STYLE_ID,
    cssText,
    "reader-custom-theme",
  );
  upsertReaderStyleElement(
    doc,
    READER_SETTINGS_STYLE_ID,
    cssText,
    "reader-custom-settings",
  );

  // Prevent mobile WebKit from silently autoshrinking reader text after load.
  doc.documentElement.style.setProperty(
    "-webkit-text-size-adjust",
    "100%",
    "important",
  );
  doc.documentElement.style.setProperty(
    "text-size-adjust",
    "100%",
    "important",
  );
  doc.body.style.setProperty("-webkit-text-size-adjust", "100%", "important");
  doc.body.style.setProperty("text-size-adjust", "100%", "important");
}
